import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { format } from 'date-fns';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Heading1,
  Heading2,
  Italic,
  List,
  ListOrdered,
  Maximize2,
  Minimize2,
  Underline as UnderlineIcon,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE } from '@/lib/typography';
import { useAuth } from '@/lib/auth';
import { usePatientTabPrefs } from '@/hooks/usePatientTabPrefs';
import {
  useCreateJournalPhrase,
  useJournalPhrases,
  useUpdateJournalPhrase,
  type JournalPhraseRow,
} from '@/hooks/useJournalPhrases';
import {
  canManageJournalPhrase,
  detectSlashTrigger,
  filterJournalPhrases,
  isPhraseShortcutConflict,
  phrasesForSlashPopup,
  type SlashTrigger,
} from '@/utils/journalPhrases';
import {
  escapeHtml,
  journalHtmlForEditor,
  journalHtmlToPlainText,
  plainTextToEditorHtml,
  shouldSetJournalEditorContent,
  storedJournalHtml,
} from '@/lib/journal/journalRichText';
import {
  JOURNAL_EDITOR_FULLSCREEN_Z_CLASS,
  JOURNAL_PHRASE_POPOVER_Z_CLASS,
} from '@/lib/journal/journalOverlayLayers';
import { useJournalEntries } from '@/hooks/useJournal';
import { JournalPhraseFormModal, type JournalPhraseFormValues } from './JournalPhraseFormModal';
import { ManageJournalPhrasesModal } from './ManageJournalPhrasesModal';
import { JournalPhraseOption } from './JournalPhraseMenu';
import { JournalFullscreenHistory } from '@/components/journal/JournalFullscreenHistory';

export interface JournalHistoryNote {
  id: string;
  title: string | null;
  date: string;
  contentMd: string;
  contentHtml: string | null;
  authorName: string | null;
}

export interface JournalEditorChange {
  html: string | null;
  text: string;
}

interface JournalRichTextEditorProps {
  clinicId: string;
  patientId?: string;
  html: string | null | undefined;
  text: string;
  onChange: (value: JournalEditorChange) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  error?: string;
  hint?: string;
  id?: string;
  className?: string;
  minHeightClass?: string;
  allowSaveSelection?: boolean;
  showFullscreenButton?: boolean;
  historyNotes?: JournalHistoryNote[];
  toolbarEnd?: ReactNode;
  insertHtmlRequest?: { html: string; key: number } | null;
}

function currentSlashTrigger(editor: Editor): SlashTrigger | null {
  const { $from } = editor.state.selection;
  if (!$from.parent.isTextblock) return null;
  return detectSlashTrigger($from.parent.textContent, $from.parentOffset);
}

function selectedEditorText(editor: Editor): string {
  const { from, to } = editor.state.selection;
  if (to <= from) return '';
  return editor.state.doc.textBetween(from, to, '\n');
}

function emitEditorValue(editor: Editor): JournalEditorChange {
  const html = storedJournalHtml(editor.getHTML());
  const text = html ? journalHtmlToPlainText(html) : editor.getText({ blockSeparator: '\n' }).trim();
  return { html, text };
}

export function JournalRichTextEditor({
  clinicId,
  patientId,
  html,
  text,
  onChange,
  placeholder,
  disabled,
  label,
  error,
  hint,
  id,
  className,
  minHeightClass = 'min-h-[16rem]',
  allowSaveSelection = true,
  showFullscreenButton = true,
  historyNotes = [],
  toolbarEnd,
  insertHtmlRequest,
}: JournalRichTextEditorProps) {
  const { t } = useTranslation();
  const { user, isMainClinicAdmin, isPlatformAdmin, isClinicAdmin } = useAuth();
  const isAdmin =
    isMainClinicAdmin || isPlatformAdmin || Boolean(clinicId && isClinicAdmin(clinicId));
  const currentUserId = user?.id ?? null;
  const inputId = id;
  const listId = useId();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const lastEmittedHtml = useRef<string | null | undefined>(undefined);
  const savedSelection = useRef<{ from: number; to: number } | null>(null);
  const pendingInsertTrigger = useRef<SlashTrigger | null>(null);
  const phrasesQuery = useJournalPhrases(clinicId);
  const createPhrase = useCreateJournalPhrase(clinicId);
  const updatePhrase = useUpdateJournalPhrase(clinicId);
  const { journalFullscreenShowHistory, setJournalFullscreenShowHistory } = usePatientTabPrefs();
  const [trigger, setTrigger] = useState<SlashTrigger | null>(null);
  const [menuIndex, setMenuIndex] = useState(0);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formError, setFormError] = useState<string | null>(null);
  const [formInitial, setFormInitial] = useState<Partial<JournalPhraseFormValues>>({});
  const [formInsertOnSave, setFormInsertOnSave] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenLayer, setFullscreenLayer] = useState<HTMLElement | null>(null);
  const [historyQuery, setHistoryQuery] = useState('');
  const [historyWidth, setHistoryWidth] = useState(320);
  const triggerRef = useRef<SlashTrigger | null>(null);
  const matchesRef = useRef<JournalPhraseRow[]>([]);
  const activeIndexRef = useRef(0);
  const insertBodyRef = useRef<(body: string) => void>(() => undefined);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const fetchHistory = Boolean(
    fullscreen && journalFullscreenShowHistory && historyNotes.length === 0 && patientId,
  );
  const historyEntriesQuery = useJournalEntries(fetchHistory ? clinicId : null, {
    patientId,
  });

  const popupPhrases = useMemo(
    () => phrasesForSlashPopup(phrasesQuery.data ?? [], currentUserId),
    [currentUserId, phrasesQuery.data],
  );
  const matches = useMemo(
    () => (trigger ? filterJournalPhrases(popupPhrases, trigger.query) : []),
    [popupPhrases, trigger],
  );
  const activeIndex = matches.length === 0 ? 0 : Math.min(menuIndex, matches.length - 1);
  triggerRef.current = trigger;
  matchesRef.current = matches;
  activeIndexRef.current = activeIndex;

  useEffect(() => {
    setMenuIndex(0);
  }, [trigger?.query]);

  const updateMenuFromEditor = useCallback((ed: Editor) => {
    const next = disabledRef.current ? null : currentSlashTrigger(ed);
    setTrigger(next);
    setSelectedText(selectedEditorText(ed));
    if (!next) {
      setMenuPos(null);
      return;
    }
    const { $from } = ed.state.selection;
    const coords = ed.view.coordsAtPos($from.start() + next.start);
    const left = Math.min(coords.left, window.innerWidth - 300);
    let top = coords.bottom + 4;
    if (top + 240 > window.innerHeight) top = coords.top - 8 - 200;
    setMenuPos({ top: Math.max(8, top), left: Math.max(8, left) });
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2] },
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
      }),
      Underline,
      Placeholder.configure({
        placeholder: placeholder ?? '',
      }),
    ],
    content: journalHtmlForEditor(html, text),
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        id: inputId ?? '',
        class: cn('journal-rich-text outline-none', minHeightClass),
        'aria-invalid': error ? 'true' : 'false',
      },
      handleKeyDown: (_view, event) => {
        const openTrigger = triggerRef.current;
        const openMatches = matchesRef.current;
        const openIndex = activeIndexRef.current;
        if (!openTrigger) return false;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setMenuIndex((index) => Math.min(index + 1, Math.max(openMatches.length - 1, 0)));
          return true;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setMenuIndex((index) => Math.max(index - 1, 0));
          return true;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setTrigger(null);
          setMenuPos(null);
          return true;
        }
        if ((event.key === 'Enter' || event.key === 'Tab') && openMatches[openIndex]) {
          event.preventDefault();
          insertBodyRef.current(openMatches[openIndex].body);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const value = emitEditorValue(ed);
      lastEmittedHtml.current = value.html;
      onChangeRef.current(value);
      updateMenuFromEditor(ed);
    },
    onSelectionUpdate: ({ editor: ed }) => {
      updateMenuFromEditor(ed);
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor || !insertHtmlRequest?.html) return;
    editor.chain().focus().insertContent(insertHtmlRequest.html).run();
    const value = emitEditorValue(editor);
    lastEmittedHtml.current = value.html;
    onChangeRef.current(value);
  }, [editor, insertHtmlRequest?.key, insertHtmlRequest?.html]);

  useEffect(() => {
    if (!editor) return;
    const incoming = journalHtmlForEditor(html, text);
    const current = editor.getHTML();
    if (
      !shouldSetJournalEditorContent({
        incoming,
        current,
        html,
        text,
        lastEmittedHtml: lastEmittedHtml.current,
      })
    ) {
      return;
    }
    editor.commands.setContent(incoming || '<p></p>', false);
    lastEmittedHtml.current = html ?? null;
  }, [editor, html, text]);

  const insertBody = useCallback(
    (body: string) => {
      if (!editor || !trigger) return;
      const { $from } = editor.state.selection;
      const from = $from.start() + trigger.start;
      const to = $from.start() + trigger.end;
      const insertHtml = body.includes('\n') ? plainTextToEditorHtml(body) : escapeHtml(body);
      editor.chain().focus().deleteRange({ from, to }).insertContent(insertHtml).run();
      setTrigger(null);
      setMenuPos(null);
    },
    [editor, trigger],
  );
  insertBodyRef.current = insertBody;

  const rememberSelection = () => {
    if (!editor) return;
    savedSelection.current = {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    };
  };

  const restoreEditorFocus = useCallback(() => {
    const sel = savedSelection.current;
    requestAnimationFrame(() => {
      if (!editor) return;
      editor.commands.focus();
      if (sel) editor.commands.setTextSelection(sel);
    });
  }, [editor]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setEditingId(null);
    restoreEditorFocus();
  }, [restoreEditorFocus]);

  const closeManage = useCallback(() => {
    setManageOpen(false);
    restoreEditorFocus();
  }, [restoreEditorFocus]);

  const openCreateForm = (initial: Partial<JournalPhraseFormValues>, insertOnSave: boolean) => {
    pendingInsertTrigger.current = insertOnSave ? trigger : null;
    rememberSelection();
    setFormMode('create');
    setEditingId(null);
    setFormInitial(initial);
    setFormInsertOnSave(insertOnSave);
    setFormError(null);
    setFormOpen(true);
    setTrigger(null);
    setMenuPos(null);
  };

  const openEditForm = (phrase: JournalPhraseRow) => {
    rememberSelection();
    setFormMode('edit');
    setEditingId(phrase.id);
    setFormInitial({
      shortcut: phrase.shortcut,
      title: phrase.title ?? '',
      body: phrase.body,
      visibility: phrase.visibility === 'shared' ? 'shared' : 'private',
    });
    setFormInsertOnSave(false);
    setFormError(null);
    setFormOpen(true);
    setTrigger(null);
    setMenuPos(null);
  };

  const openManage = () => {
    rememberSelection();
    setTrigger(null);
    setMenuPos(null);
    setManageOpen(true);
  };

  const handleFormSubmit = async (values: JournalPhraseFormValues) => {
    try {
      if (formMode === 'edit' && editingId) {
        await updatePhrase.mutateAsync({ id: editingId, ...values });
        setFormOpen(false);
        setEditingId(null);
        setFormError(null);
        toast.success(t('journal.phrases.updated'));
        restoreEditorFocus();
        return;
      }
      const created = await createPhrase.mutateAsync(values);
      setFormOpen(false);
      setFormError(null);
      toast.success(t('journal.phrases.created'));
      const savedTrigger = pendingInsertTrigger.current;
      pendingInsertTrigger.current = null;
      if (formInsertOnSave && savedTrigger && editor) {
        setTrigger(savedTrigger);
        requestAnimationFrame(() => insertBody(created.body));
      } else {
        restoreEditorFocus();
      }
    } catch (err) {
      setFormError(
        isPhraseShortcutConflict(err)
          ? t('journal.phrases.shortcut_conflict')
          : t('common.error'),
      );
    }
  };

  const exitFullscreen = useCallback(() => {
    if (!editor) {
      setFullscreen(false);
      return;
    }
    savedSelection.current = {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    };
    setFullscreen(false);
    requestAnimationFrame(() => {
      editor.commands.focus();
      if (savedSelection.current) editor.commands.setTextSelection(savedSelection.current);
    });
  }, [editor]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (trigger || formOpen || manageOpen) return;
      event.preventDefault();
      exitFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exitFullscreen, formOpen, fullscreen, manageOpen, trigger]);

  useEffect(() => {
    if (!trigger) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      setTrigger(null);
      setMenuPos(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [trigger]);

  const menuOpen = Boolean(trigger && menuPos && !disabled && !formOpen && !manageOpen);

  const startHistoryResize = (event: ReactMouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = historyWidth;
    const onMove = (move: MouseEvent) => {
      setHistoryWidth(Math.min(480, Math.max(240, startWidth + (move.clientX - startX))));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const resolvedHistory = useMemo<JournalHistoryNote[]>(() => {
    if (historyNotes.length > 0) return historyNotes;
    return (historyEntriesQuery.data ?? [])
      .filter((entry) => !entry.discarded_at)
      .map((entry) => ({
        id: entry.id,
        title: entry.title,
        date: format(new Date(entry.encounter_at ?? entry.entry_date), 'PPP'),
        contentMd: entry.content_md,
        contentHtml: entry.content_html,
        authorName: entry.author_name,
      }));
  }, [historyEntriesQuery.data, historyNotes]);

  const filteredHistory = useMemo(() => {
    const needle = historyQuery.trim().toLowerCase();
    if (!needle) return resolvedHistory;
    return resolvedHistory.filter((note) => {
      const hay = `${note.title ?? ''} ${note.contentMd} ${note.authorName ?? ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [historyQuery, resolvedHistory]);

  const toolbar = (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1 border-b border-ds-line bg-ds-toolbar px-2 py-1',
        toolbarEnd && 'rte-tb',
      )}
      role="toolbar"
      aria-label={t('journal.editor.toolbar')}
    >
      <ToolbarButton
        label={t('journal.editor.bold')}
        shortcut="Ctrl+B"
        active={editor?.isActive('bold')}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label={t('journal.editor.italic')}
        shortcut="Ctrl+I"
        active={editor?.isActive('italic')}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        dataTb="underline"
        label={t('journal.editor.underline')}
        shortcut="Ctrl+U"
        active={editor?.isActive('underline')}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label={t('journal.editor.heading1')}
        active={editor?.isActive('heading', { level: 1 })}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        dataTb="heading2"
        label={t('journal.editor.heading2')}
        active={editor?.isActive('heading', { level: 2 })}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label={t('journal.editor.bullet_list')}
        active={editor?.isActive('bulletList')}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        label={t('journal.editor.ordered_list')}
        active={editor?.isActive('orderedList')}
        disabled={disabled}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      {toolbarEnd ? <span className="tb-sep" aria-hidden /> : null}
      {toolbarEnd}
      {showFullscreenButton && !fullscreen && (
        <button
          type="button"
          data-tb="fullscreen"
          className="ml-auto inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-ds-muted hover:bg-ds-neutral-tint"
          onClick={() => {
            rememberSelection();
            setFullscreen(true);
            requestAnimationFrame(() => restoreEditorFocus());
          }}
        >
          <Maximize2 className="h-3.5 w-3.5" />
          {t('journal.editor.fullscreen')}
        </button>
      )}
    </div>
  );

  const editorSurface = (
    <div
      className={cn(
        'rte overflow-hidden rounded-md border border-ds-field bg-ds-card shadow-card focus-within:border-ds-teal focus-within:ring-1 focus-within:ring-ds-teal',
        error && 'border-ds-red focus-within:border-ds-red focus-within:ring-ds-red',
        fullscreen && 'rounded-none border-0 shadow-none',
        className,
      )}
    >
      {toolbar}
      <EditorContent
        editor={editor}
        className={cn(
          fullscreen
            ? 'journal-rich-text-page px-2 py-4'
            : cn('px-3 py-2', minHeightClass),
        )}
      />
    </div>
  );

  const phraseChrome = (
    <>
      {allowSaveSelection && !disabled && (
        <div className="mt-1">
          <Button
            type="button"
            variant="link"
            disabled={!selectedText.trim()}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => openCreateForm({ body: selectedText, visibility: 'private' }, false)}
          >
            {t('journal.phrases.save_selection')}
          </Button>
        </div>
      )}
      {menuOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-label={t('journal.phrases.list_label')}
            className={cn(
              'fixed w-80 overflow-hidden rounded-lg border border-ds-line-strong bg-ds-card shadow-pop',
              JOURNAL_PHRASE_POPOVER_Z_CLASS,
            )}
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <div className="max-h-56 overflow-y-auto py-1">
              {matches.length === 0 ? (
                <p className="px-3 py-2 text-sm text-ds-muted">
                  {t('journal.phrases.empty')}
                </p>
              ) : (
                matches.map((phrase, index) => (
                  <JournalPhraseOption
                    key={phrase.id}
                    phrase={phrase}
                    active={index === activeIndex}
                    canEdit={canManageJournalPhrase(phrase, currentUserId, isAdmin)}
                    onHover={() => setMenuIndex(index)}
                    onInsert={() => insertBody(phrase.body)}
                    onEdit={() => openEditForm(phrase)}
                    sharedLabel={t('journal.phrases.shared')}
                    privateLabel={t('journal.phrases.private')}
                    editLabel={t('common.edit')}
                  />
                ))
              )}
            </div>
            <div className="border-t border-ds-line">
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm text-ds-teal hover:bg-ds-sunk hover:bg-ds-neutral-tint"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() =>
                  openCreateForm(
                    {
                      shortcut: trigger?.query ?? '',
                      visibility: 'private',
                    },
                    true,
                  )
                }
              >
                {t('journal.phrases.create')}
              </button>
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm text-ds-teal hover:bg-ds-sunk hover:bg-ds-neutral-tint"
                onMouseDown={(event) => event.preventDefault()}
                onClick={openManage}
              >
                {t('journal.phrases.manage')}
              </button>
            </div>
          </div>,
          fullscreen && fullscreenLayer ? fullscreenLayer : document.body,
        )}
      <JournalPhraseFormModal
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        pending={createPhrase.isPending || updatePhrase.isPending}
        error={formError}
        onClose={closeForm}
        onSubmit={(values) => void handleFormSubmit(values)}
      />
      <ManageJournalPhrasesModal clinicId={clinicId} open={manageOpen} onClose={closeManage} />
    </>
  );

  const inlineEditor = (
    <div>
      {label && (
        <label htmlFor={inputId} className={cn('block', FORM_LABEL_TYPE)}>
          {label}
        </label>
      )}
      <div className={cn(label && 'mt-1')}>{editorSurface}</div>
      {hint && !error && (
        <p id={inputId ? `${inputId}-hint` : undefined} className={cn('mt-1', FORM_HINT_TYPE)}>
          {hint}
        </p>
      )}
      {error && (
        <p id={inputId ? `${inputId}-error` : undefined} className={cn('mt-1', FORM_ERROR_TYPE)}>
          {error}
        </p>
      )}
      {phraseChrome}
    </div>
  );

  if (!fullscreen) return inlineEditor;

  return createPortal(
        <div
          ref={setFullscreenLayer}
          className={cn('fixed inset-0 flex flex-col bg-ds-bg', JOURNAL_EDITOR_FULLSCREEN_Z_CLASS)}
        >
          <div className="flex shrink-0 items-center gap-3 border-b border-ds-line bg-ds-card px-4 py-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-ds-text hover:bg-ds-neutral-tint hover:bg-ds-row-hover"
              onClick={exitFullscreen}
            >
              <Minimize2 className="h-4 w-4" />
              {t('journal.editor.exit_fullscreen')}
            </button>
            <label className="ml-auto inline-flex items-center gap-2 text-sm text-ds-text">
              <input
                type="checkbox"
                checked={journalFullscreenShowHistory}
                onChange={(event) => setJournalFullscreenShowHistory(event.target.checked)}
              />
              {t('journal.editor.show_history')}
            </label>
            <button
              type="button"
              className="rounded p-1 text-ds-muted hover:bg-ds-neutral-tint"
              aria-label={t('common.close')}
              onClick={exitFullscreen}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex min-h-0 flex-1">
            {journalFullscreenShowHistory && (
              <>
                <aside
                  className="flex min-h-0 flex-col border-r border-ds-line bg-ds-card"
                  style={{ width: historyWidth }}
                >
                  <JournalFullscreenHistory
                    clinicId={clinicId}
                    patientId={patientId}
                    userId={currentUserId}
                    notes={filteredHistory}
                    query={historyQuery}
                    onQueryChange={setHistoryQuery}
                  />
                </aside>
                <div
                  role="separator"
                  aria-orientation="vertical"
                  className="w-1.5 cursor-col-resize bg-ds-neutral-tint hover:bg-ds-teal-line bg-ds-card"
                  onMouseDown={startHistoryResize}
                />
              </>
            )}
            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
              <div className="mx-auto min-h-full max-w-3xl bg-ds-card px-12 py-10 shadow-card">
                {editorSurface}
                {phraseChrome}
              </div>
            </div>
          </div>
        </div>,
        document.body,
  );
}

function ToolbarButton({
  label,
  shortcut,
  active,
  disabled,
  onClick,
  children,
  dataTb,
}: {
  label: string;
  shortcut?: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
  dataTb?: string;
}) {
  return (
    <button
      type="button"
      data-tb={dataTb}
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'rounded p-1.5 text-ds-muted hover:bg-ds-neutral-tint disabled:opacity-40',
        active && 'bg-ds-teal-tint text-ds-teal',
      )}
    >
      {children}
    </button>
  );
}
